import { useEffect, useState, type CSSProperties } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, KeyRound, Pencil, Smartphone, Unlink } from "lucide-react";
import { Modal } from "../components/Modal";
import { PageHeader } from "../components/PageHeader";
import { ChangePasswordForm } from "../components/ChangePasswordForm";
import { useTheme } from "../theme/ThemeContext";
import { useAuth } from "../auth/AuthContext";
import { useBilling } from "../data/BillingContext";
import { usePersonalCenter } from "../data/PersonalCenterContext";
import {
  apiErrorMessage,
  cloudGetMe,
  cloudGetTenantInfo,
  cloudPhoneBind,
  cloudPhoneSendCode,
  cloudWechatUnbind,
} from "../api/client";
import { useToast } from "../toast/ToastContext";
import { planCatalog } from "../data/plan";

const PLAN_ORDER = ["starter", "growth", "scale"] as const;

const langs = [
  { id: "zh", label: "简体中文", hint: "ZH" },
  { id: "en", label: "English", hint: "EN" },
  { id: "de", label: "Deutsch", hint: "DE" },
];

const accents = [
  { id: "Azure", color: "hsl(215 90% 45%)" },
  { id: "Teal", color: "#14b8a6" },
  { id: "Emerald", color: "#10b981" },
  { id: "Amber", color: "#f59e0b" },
  { id: "Violet", color: "#8b5cf6" },
  { id: "Rose", color: "#f43f5e" },
];

function profileFromAuth(email: string | null, displayName: string | null) {
  const addr = (email || "").trim();
  const name = (displayName || "").trim();
  // Prefer server/WeChat display name; fall back to email local part
  const local =
    name ||
    (addr.endsWith("@wechat.udhub.local") ? "微信用户" : addr.split("@")[0]) ||
    "用户";
  return {
    firstName: local,
    lastName: "",
    email: addr,
  };
}

export function PersonalCenterContent({
  embedded = true,
}: {
  /** When false, skip closing the personal-center drawer (standalone page). */
  embedded?: boolean;
}) {
  const {
    closePersonalCenter,
    phoneBindRequested,
    clearPhoneBindRequest,
  } = usePersonalCenter();
  const navigate = useNavigate();
  const {
    org,
    email: authEmail,
    name: authName,
    tenantId,
    role,
    markPhoneBound,
  } = useAuth();
  const { plan, upgradePlan, plans, subscription } = useBilling();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const [lang, setLang] = useState("zh");
  const [accent, setAccent] = useState("Azure");
  const [session, setSession] = useState("1");
  const [pwdOpen, setPwdOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const [wxBound, setWxBound] = useState(false);
  const [wxUnbindOpen, setWxUnbindOpen] = useState(false);
  const [wxUnbindPassword, setWxUnbindPassword] = useState("");
  const [wxUnbindBusy, setWxUnbindBusy] = useState(false);
  const [wxUnbindError, setWxUnbindError] = useState("");
  const [phoneBound, setPhoneBound] = useState(false);
  const [boundPhone, setBoundPhone] = useState<string | null>(null);
  const [phoneBindOpen, setPhoneBindOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneCooldown, setPhoneCooldown] = useState(0);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [profile, setProfile] = useState(() =>
    profileFromAuth(authEmail, authName),
  );
  const [tenantName, setTenantName] = useState<string>("");
  const isPlatformAdmin = role === "platform_admin";
  const isWxPlaceholder = Boolean(
    authEmail?.endsWith("@wechat.udhub.local"),
  );

  const workspaceId = tenantId || org.id || "—";

  useEffect(() => {
    setProfile(profileFromAuth(authEmail, authName));
  }, [authEmail, authName]);

  useEffect(() => {
    void cloudGetTenantInfo()
      .then((t) => setTenantName(t.name))
      .catch(() => setTenantName(""));
  }, []);

  useEffect(() => {
    void cloudGetMe()
      .then((me) => {
        setWxBound(Boolean(me.wxBound));
        setPhoneBound(Boolean(me.phoneBound));
        setBoundPhone(me.phone || null);
      })
      .catch(() => {
        setWxBound(false);
        setPhoneBound(false);
        setBoundPhone(null);
      });
  }, [authEmail]);

  useEffect(() => {
    if (phoneCooldown <= 0) return;
    const t = window.setTimeout(
      () => setPhoneCooldown((c) => c - 1),
      1000,
    );
    return () => window.clearTimeout(t);
  }, [phoneCooldown]);

  useEffect(() => {
    if (!phoneBindRequested) return;
    setPhoneBindOpen(true);
    clearPhoneBindRequest();
  }, [phoneBindRequested, clearPhoneBindRequest]);

  const displayName =
    `${profile.lastName}${profile.firstName}`.trim() ||
    profile.email ||
    "用户";
  const avatarLetter = (profile.lastName || profile.firstName || profile.email || "?").slice(0, 1);

  const openChangePassword = () => {
    if (embedded) {
      closePersonalCenter();
      navigate("/settings/password");
      return;
    }
    setPwdOpen(true);
  };

  const openWxUnbind = () => {
    if (!wxBound) return;
    if (isWxPlaceholder) {
      toast("请先完善真实邮箱后再解绑微信，否则将无法登录");
      return;
    }
    setWxUnbindPassword("");
    setWxUnbindError("");
    setWxUnbindOpen(true);
  };

  const submitWxUnbind = async () => {
    if (!wxUnbindPassword.trim()) {
      setWxUnbindError("请输入当前密码");
      return;
    }
    setWxUnbindBusy(true);
    setWxUnbindError("");
    try {
      const res = await cloudWechatUnbind(wxUnbindPassword);
      setWxBound(false);
      setWxUnbindOpen(false);
      toast(res.message || "已解绑微信");
    } catch (err) {
      setWxUnbindError(apiErrorMessage(err) || "解绑失败");
    } finally {
      setWxUnbindBusy(false);
    }
  };

  const openPhoneBind = () => {
    setPhoneInput(boundPhone || "");
    setPhoneCode("");
    setPhoneError("");
    setPhoneHint("");
    setPhoneBindOpen(true);
  };

  const sendPhoneBindCode = async () => {
    if (phoneSending || phoneCooldown > 0 || !phoneInput.trim()) return;
    setPhoneError("");
    setPhoneHint("正在发送…");
    setPhoneSending(true);
    try {
      const res = await cloudPhoneSendCode(phoneInput, "bind");
      setPhoneHint(res.message || "验证码已发送");
      if (res.delivered !== false) setPhoneCooldown(60);
    } catch (err) {
      setPhoneHint("");
      const msg = apiErrorMessage(err) || "发送失败";
      setPhoneError(msg);
      const m = msg.match(/(\d+)\s*秒/);
      if (m) setPhoneCooldown(Math.max(1, Number(m[1])));
    } finally {
      setPhoneSending(false);
    }
  };

  const submitPhoneBind = async () => {
    if (!phoneInput.trim() || !phoneCode.trim()) {
      setPhoneError("请填写手机号和验证码");
      return;
    }
    setPhoneBusy(true);
    setPhoneError("");
    try {
      const res = await cloudPhoneBind(phoneInput, phoneCode);
      setPhoneBound(true);
      setBoundPhone(res.phone);
      markPhoneBound(true);
      setPhoneBindOpen(false);
      toast(res.message || "手机号已绑定");
    } catch (err) {
      setPhoneError(apiErrorMessage(err) || "绑定失败");
    } finally {
      setPhoneBusy(false);
    }
  };

  const currentPlanId = subscription?.plan ?? plan.id;
  const curRank = PLAN_ORDER.indexOf(
    currentPlanId as (typeof PLAN_ORDER)[number],
  );
  const upgradeTargets = (
    plans.length > 0
      ? plans
      : Object.values(planCatalog).map((p) => ({
          id: p.id,
          label: p.label,
          maxProjects: p.projectQuota,
          maxDevices: p.deviceQuota,
        }))
  ).filter((p) => {
    const next = PLAN_ORDER.indexOf(p.id as (typeof PLAN_ORDER)[number]);
    return curRank >= 0 && next > curRank;
  });

  const submitUpgrade = async (planId: string) => {
    setUpgradeBusy(true);
    try {
      await upgradePlan(planId);
      toast("套餐已更新");
      setUpgradeOpen(false);
    } catch (err) {
      toast(apiErrorMessage(err, "升级失败，请联系平台支持"));
    } finally {
      setUpgradeBusy(false);
    }
  };

  return (
    <div className="settings-page personal-center-panel">
      <div className="settings-grid">
        <section className="settings-card settings-card-profile">
          <header className="settings-card-head">
            <h2>个人资料</h2>
            <button
              className="btn btn-ghost btn-xs"
              type="button"
              onClick={() => setProfileOpen(true)}
            >
              <Pencil size={14} />
              编辑
            </button>
          </header>
          <div className="settings-profile">
            <div className="settings-avatar" aria-hidden>
              {avatarLetter}
            </div>
            <div className="settings-profile-meta">
              <strong>{displayName}</strong>
              <span>
                {profile.email.endsWith("@wechat.udhub.local")
                  ? "微信登录账号（尚未绑定邮箱）"
                  : profile.email}
              </span>
              {profile.email.endsWith("@wechat.udhub.local") ? (
                <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>
                  建议完善真实邮箱，便于找回与通知。完整绑定能力即将上线。
                </p>
              ) : null}
            </div>
          </div>
        </section>

        <section className="settings-card">
          <header className="settings-card-head">
            <h2>组织</h2>
          </header>
          <dl className="settings-dl">
            <div>
              <dt>租户</dt>
              <dd>{tenantName || org.name}</dd>
            </div>
            <div>
              <dt>工作区 ID</dt>
              <dd className="settings-ws-id" title={workspaceId}>
                {workspaceId}
              </dd>
            </div>
          </dl>
          {!isPlatformAdmin ? (
            <div className="settings-plan-row">
              <div className="settings-plan-meta">
                <span className="settings-plan-label">当前套餐</span>
                <strong>{subscription?.label ?? plan.label}</strong>
              </div>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                disabled={upgradeTargets.length === 0}
                title={
                  upgradeTargets.length === 0 ? "已是最高套餐" : "升级套餐"
                }
                onClick={() => setUpgradeOpen(true)}
              >
                {upgradeTargets.length === 0 ? "已是最高套餐" : "升级"}
              </button>
            </div>
          ) : null}
        </section>

        <section className="settings-card">
          <header className="settings-card-head">
            <h2>语言</h2>
          </header>
          <div className="settings-lang-row">
            {langs.map((l) => (
              <button
                key={l.id}
                type="button"
                className={`settings-option settings-option-compact${lang === l.id ? " active" : ""}`}
                onClick={() => setLang(l.id)}
              >
                <span className="settings-option-code">{l.hint}</span>
                <span>{l.label}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="settings-card">
          <header className="settings-card-head">
            <h2>主题</h2>
          </header>
          <div className="settings-theme-modes">
            <button
              type="button"
              className={`settings-option${theme === "dark" ? " active" : ""}`}
              onClick={() => setTheme("dark")}
            >
              深色
            </button>
            <button
              type="button"
              className={`settings-option${theme === "light" ? " active" : ""}`}
              onClick={() => setTheme("light")}
            >
              浅色
            </button>
          </div>
          <div className="settings-accent-row" role="group" aria-label="强调色">
            {accents.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`settings-accent${accent === a.id ? " active" : ""}`}
                style={{ "--accent-swatch": a.color } as CSSProperties}
                title={a.id}
                aria-label={a.id}
                aria-pressed={accent === a.id}
                onClick={() => setAccent(a.id)}
              />
            ))}
          </div>
        </section>

        <section className="settings-card">
          <header className="settings-card-head">
            <h2>会话时长</h2>
          </header>
          <p className="settings-card-desc">
            登录后多久需要重新认证。
          </p>
          <select
            className="settings-select"
            value={session}
            onChange={(e) => setSession(e.target.value)}
            aria-label="会话时长"
          >
            <option value="1">1 天</option>
            <option value="7">7 天</option>
            <option value="30">30 天</option>
          </select>
        </section>

        <section className="settings-card">
          <header className="settings-card-head">
            <h2>安全</h2>
          </header>
          <p className="settings-card-desc">保护账户登录凭据。</p>
          <button
            className="settings-nav-row"
            type="button"
            onClick={openChangePassword}
          >
            <span className="settings-nav-row__icon" aria-hidden>
              <KeyRound size={16} strokeWidth={1.85} />
            </span>
            <span className="settings-nav-row__copy">
              <strong>修改密码</strong>
              <span>定期更新口令，降低账户风险</span>
            </span>
            <ChevronRight
              className="settings-nav-row__chevron"
              size={18}
              strokeWidth={1.75}
              aria-hidden
            />
          </button>
          <button
            className="settings-nav-row"
            type="button"
            onClick={openPhoneBind}
          >
            <span className="settings-nav-row__icon" aria-hidden>
              <Smartphone size={16} strokeWidth={1.85} />
            </span>
            <span className="settings-nav-row__copy">
              <strong>手机号</strong>
              <span>
                {phoneBound && boundPhone
                  ? `已绑定 ${boundPhone.replace(/^(\d{3})\d{4}(\d{4})$/, "$1****$2")} · 可更换`
                  : "未绑定 · 绑定后可用短信验证码登录"}
              </span>
            </span>
            <ChevronRight
              className="settings-nav-row__chevron"
              size={18}
              strokeWidth={1.75}
              aria-hidden
            />
          </button>
          <button
            className="settings-nav-row"
            type="button"
            onClick={openWxUnbind}
            disabled={!wxBound}
            aria-disabled={!wxBound}
          >
            <span className="settings-nav-row__icon" aria-hidden>
              <Unlink size={16} strokeWidth={1.85} />
            </span>
            <span className="settings-nav-row__copy">
              <strong>微信登录</strong>
              <span>
                {wxBound
                  ? isWxPlaceholder
                    ? "已绑定 · 完善邮箱后方可解绑"
                    : "已绑定 · 点击解绑"
                  : "未绑定 · 可在登录页扫码绑定"}
              </span>
            </span>
            {wxBound ? (
              <ChevronRight
                className="settings-nav-row__chevron"
                size={18}
                strokeWidth={1.75}
                aria-hidden
              />
            ) : null}
          </button>
        </section>
      </div>

      {isPlatformAdmin ? (
        <section className="settings-card settings-card-wide">
          <header className="settings-card-head">
            <div>
              <h2>站点配置</h2>
              <p className="settings-card-desc">
                SMTP 发信与公开站点 URL 已迁至独立「站点配置」页。
              </p>
            </div>
            <Link
              className="btn btn-primary"
              to="/admin/site-config"
              onClick={() => {
                if (embedded) closePersonalCenter();
              }}
            >
              打开站点配置
            </Link>
          </header>
        </section>
      ) : null}

      <Modal open={profileOpen} title="编辑资料" onClose={() => setProfileOpen(false)}>
        <div className="form-grid">
          <div className="field">
            <label>名 *</label>
            <input
              value={profile.firstName}
              onChange={(e) =>
                setProfile({ ...profile, firstName: e.target.value })
              }
            />
          </div>
          <div className="field">
            <label>姓 *</label>
            <input
              value={profile.lastName}
              onChange={(e) =>
                setProfile({ ...profile, lastName: e.target.value })
              }
            />
          </div>
          <div className="field full">
            <label>邮箱 *</label>
            <input value={profile.email} readOnly disabled />
          </div>
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setProfileOpen(false)}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setProfileOpen(false)}
          >
            保存
          </button>
        </div>
      </Modal>

      <Modal open={pwdOpen} title="修改密码" onClose={() => setPwdOpen(false)}>
        <ChangePasswordForm
          compact
          accountEmail={authEmail}
          onCancel={() => setPwdOpen(false)}
        />
      </Modal>

      <Modal
        open={phoneBindOpen}
        title={phoneBound ? "更换手机号" : "绑定手机号"}
        onClose={() => {
          if (!phoneBusy) setPhoneBindOpen(false);
        }}
      >
        <p className="muted" style={{ marginTop: 0 }}>
          绑定须短信验证码确认。签名「南京中枢」；绑定后可在登录页用手机号登录。
        </p>
        <div className="field">
          <label htmlFor="bind-phone">手机号</label>
          <input
            id="bind-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="11 位手机号"
            value={phoneInput}
            disabled={phoneBusy}
            onChange={(e) => setPhoneInput(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="bind-phone-code">验证码</label>
          <div className="aliyun-field-otp">
            <input
              id="bind-phone-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="6 位验证码"
              value={phoneCode}
              disabled={phoneBusy}
              onChange={(e) =>
                setPhoneCode(e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") void submitPhoneBind();
              }}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm aliyun-otp-send"
              disabled={
                phoneBusy ||
                phoneSending ||
                phoneCooldown > 0 ||
                !phoneInput.trim()
              }
              onClick={() => void sendPhoneBindCode()}
            >
              {phoneSending
                ? "发送中…"
                : phoneCooldown > 0
                  ? `${phoneCooldown}s`
                  : "获取验证码"}
            </button>
          </div>
        </div>
        {phoneHint ? <p className="muted">{phoneHint}</p> : null}
        {phoneError ? (
          <p className="form-error" role="alert">
            {phoneError}
          </p>
        ) : null}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={phoneBusy}
            onClick={() => setPhoneBindOpen(false)}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={phoneBusy}
            onClick={() => void submitPhoneBind()}
          >
            {phoneBusy ? "提交中…" : "确认绑定"}
          </button>
        </div>
      </Modal>

      <Modal
        open={wxUnbindOpen}
        title="解绑微信"
        onClose={() => {
          if (!wxUnbindBusy) setWxUnbindOpen(false);
        }}
      >
        <p className="muted" style={{ marginTop: 0 }}>
          解绑后将无法用该微信扫码登录，请确认仍可用邮箱密码登录。
        </p>
        <div className="field">
          <label htmlFor="wx-unbind-password">当前密码</label>
          <input
            id="wx-unbind-password"
            type="password"
            autoComplete="current-password"
            value={wxUnbindPassword}
            disabled={wxUnbindBusy}
            onChange={(e) => setWxUnbindPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitWxUnbind();
            }}
          />
        </div>
        {wxUnbindError ? (
          <p className="form-error" role="alert">
            {wxUnbindError}
          </p>
        ) : null}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={wxUnbindBusy}
            onClick={() => setWxUnbindOpen(false)}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={wxUnbindBusy}
            onClick={() => void submitWxUnbind()}
          >
            {wxUnbindBusy ? "解绑中…" : "确认解绑"}
          </button>
        </div>
      </Modal>

      <Modal
        open={upgradeOpen}
        title="升级套餐"
        onClose={() => setUpgradeOpen(false)}
      >
        <p className="muted">选择更高套餐。正式计费开通仍可由平台运营协助。</p>
        <div className="settings-upgrade-list">
          {upgradeTargets.map((p) => (
            <button
              key={p.id}
              type="button"
              className="settings-upgrade-option"
              disabled={upgradeBusy}
              onClick={() => void submitUpgrade(p.id)}
            >
              <strong>{p.label}</strong>
              {p.maxProjects != null ? (
                <span className="muted">
                  项目 {p.maxProjects} · 设备 {p.maxDevices}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setUpgradeOpen(false)}
          >
            取消
          </button>
        </div>
      </Modal>
    </div>
  );
}

/** Standalone account settings page. */
export function SettingsPage() {
  return (
    <div className="page page-shell--flow">
      <PageHeader
        eyebrow="账户"
        title="个人中心"
        description="资料、主题偏好与登录安全"
      />
      <PersonalCenterContent embedded={false} />
    </div>
  );
}
